import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { buildApiUrl } from '../config/api';
import { databaseCategoryToDisplayName } from '../data/serviceCategoryDbMapping';
import { getHeroCategoryMuiIcon } from '../data/heroServiceCategoryMuiIcons';
import { isMobileViewport } from '../util/isMobileViewport';
import { scheduleDeferred } from '../util/scheduleDeferred';

export type HeroSearchServiceOption = {
    name: string;
    icon: ReactNode;
    /** When true, find-experts lists every expert in the city (no `service.category` filter). */
    allExperts?: true;
};

type FetchedCategory = {
    /** Raw key from `/api/service` — passed to `getCategoryIcon`. */
    categoryName: string;
    displayName: string;
};

/**
 * Loads top-level service categories from `/api/service` for the homepage hero search.
 * Shows MUI icons first; Font Awesome (ServiceIcons) is lazy-loaded after the hero is interactive.
 */
export function useHeroSearchServices(allExpertsOption: HeroSearchServiceOption) {
    const [categories, setCategories] = useState<FetchedCategory[]>([]);
    const [loading, setLoading] = useState(true);
    const [getCategoryIcon, setGetCategoryIcon] = useState<((name: string) => ReactNode) | null>(null);
    const fontAwesomeLoadStarted = useRef(false);

    const loadFontAwesomeIcons = useCallback(() => {
        if (fontAwesomeLoadStarted.current) {
            return;
        }
        fontAwesomeLoadStarted.current = true;

        void import('../components/icons/ServiceIcons')
            .then(({ getCategoryIcon: resolveCategoryIcon }) => {
                setGetCategoryIcon(() => resolveCategoryIcon);
            })
            .catch((error) => {
                fontAwesomeLoadStarted.current = false;
                console.error('Error loading hero category icons:', error);
            });
    }, []);

    // After first paint, upgrade to Font Awesome icons (same as Services menu) without blocking LCP.
    useEffect(() => {
        scheduleDeferred(() => loadFontAwesomeIcons());
    }, [loadFontAwesomeIcons]);

    useEffect(() => {
        let cancelled = false;

        const runFetch = () => {
            if (cancelled) {
                return;
            }

            void fetch(buildApiUrl('/api/service'))
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }
                return response.json();
            })
            .then((data: { success?: boolean; categories?: Record<string, unknown> }) => {
                if (cancelled || !data.success || !data.categories) {
                    return;
                }

                setCategories(
                    Object.keys(data.categories).map((categoryName) => ({
                        categoryName,
                        displayName: databaseCategoryToDisplayName(categoryName),
                    })),
                );
            })
            .catch((error) => {
                console.error('Error fetching hero search services:', error);
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        };

        // On mobile, defer category fetch until after the hero paints (LCP).
        if (isMobileViewport()) {
            scheduleDeferred(runFetch, 2500);
        } else {
            runFetch();
        }

        return () => {
            cancelled = true;
        };
    }, []);

    const heroSearchOptions = useMemo((): HeroSearchServiceOption[] => {
        return [
            allExpertsOption,
            ...categories.map(({ categoryName, displayName }) => ({
                name: displayName,
                icon: getCategoryIcon
                    ? getCategoryIcon(categoryName)
                    : getHeroCategoryMuiIcon(categoryName),
            })),
        ];
    }, [allExpertsOption, categories, getCategoryIcon]);

    return {
        heroSearchOptions,
        loading,
        /** Call when the user opens the hero service picker — starts FA load if not already queued. */
        onHeroSearchOpen: loadFontAwesomeIcons,
    };
}
